import PropTypes from 'prop-types';
import { Info } from '@phosphor-icons/react';
import { breakpoints, Inline, Tooltip, vars } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css, cx, resetButtonStyle } from '@eti/styles';
import { DangerousHtmlContent } from '@eti/components';
import { constants } from '@eti/utils';
import { UNKNOWN } from './utils/passnflyStatuses';
import { STATUS_TO_TEXT_KEY } from './utils/constants';

const statusStyles = (color, isBold) => css`
  color: ${color};
  font-weight: ${isBold ? 700 : 400};
`;

const tooltipButton = css`
  border: 2px solid transparent;
  border-radius: 50%;
  display: inline-flex;

  &:focus-visible {
    border-color: ${vars.colors.focusRing};
    transition: all 0.2s ease;
  }

  @media (min-width: ${breakpoints._768}) {
    cursor: default;
  }
`;

const outerInlineStyles = css`
  @media (min-width: ${breakpoints._560}) {
    align-items: center;
  }
`;

const StatusPerPassenger = ({
  dataTestIdPrefix,
  operatingCarrierName,
  passenger,
  shouldShowTextForAllPassengers,
}) => {
  const { t } = useTranslation();
  const { text, tooltip } = STATUS_TO_TEXT_KEY[passenger.status];
  const passengerHeadingText = shouldShowTextForAllPassengers
    ? t('OrderDetails.CheckIn.Status.AllPassengers.Label')
    : passenger.fullName;
  const testIdPrefix = `${dataTestIdPrefix}-passenger-${passenger.passengerId}`;

  return (
    <Inline
      className={outerInlineStyles}
      collapseBelow={breakpoints._560}
      data-testid={testIdPrefix}
      key={`${passenger.passengerId}-checkin-status`}
      noWrap
      spacing={{ [breakpoints._0]: 8, [breakpoints._560]: 16 }}
    >
      <div
        className={cx({
          [constants.MOUSE_FLOW_CLASS_NAMES]: !shouldShowTextForAllPassengers,
        })}
        data-testid={`${testIdPrefix}-heading`}
      >
        {passengerHeadingText}
      </div>
      <Inline alignY="center" spacing={4}>
        <div
          className={statusStyles(text.color, passenger.status !== UNKNOWN)}
          data-testid={`${testIdPrefix}-status`}
        >
          {t(text.key)}
        </div>
        {tooltip && (
          <Tooltip
            content={<DangerousHtmlContent content={t(tooltip.key, operatingCarrierName)} />}
            data-testid={`${testIdPrefix}-tooltip`}
            title={t(text.key)}
          >
            <button
              className={cx(resetButtonStyle, tooltipButton)}
              data-testid={`${testIdPrefix}-tooltip-button`}
              type="button"
            >
              <Info
                color={tooltip.color}
                data-testid={`${testIdPrefix}-tooltip-button-icon`}
                size={20}
                weight="fill"
              />
            </button>
          </Tooltip>
        )}
      </Inline>
    </Inline>
  );
};

StatusPerPassenger.propTypes = {
  dataTestIdPrefix: PropTypes.string,
  operatingCarrierName: PropTypes.string,
  passenger: PropTypes.shape({
    fullName: PropTypes.string,
    passengerId: PropTypes.string,
    status: PropTypes.string,
  }),
  shouldShowTextForAllPassengers: PropTypes.bool,
};

export default StatusPerPassenger;
